import { useState, useEffect, useCallback } from 'react'
import { Box, Button, Heading, HStack, SimpleGrid, IconButton } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { Plus, LayoutGrid, Table } from 'lucide-react'
import { useProperties, useDeleteProperty } from '../hooks/useProperties'
import { PropertyCard } from '../components/PropertyCard'
import { PropertyTable } from '../components/PropertyTable'
import { PropertyCreateModal } from '../components/PropertyCreateModal'
import { PropertyDetailDrawer } from '../components/PropertyDetailDrawer'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import { EmptyState } from '@/shared/components/EmptyState'
import { useAuth } from '@/features/auth/hooks/useAuth'

type ViewMode = 'cards' | 'table'

export function PropertiesPage() {
  const { t } = useTranslation()
  const { data: properties, isLoading, error, refetch } = useProperties()
  const { data: auth } = useAuth()
  const deleteProperty = useDeleteProperty()
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('table')
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // Check delete permissions for each residence
  // Only ADMIN and MANAGER can delete residences
  const canDeleteResidence = useCallback((residenceId: string) => {
    if (!auth) return false

    // ADMIN can delete all residences
    if (auth.isAdmin) return true

    // MANAGER can delete their managed residences
    return auth.isManager(residenceId)
  }, [auth])

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('properties.delete_confirm'))) return

    setDeletingId(id)
    try {
      await deleteProperty.mutateAsync(id)
      // Close drawer if the deleted property was selected
      if (selectedPropertyId === id) {
        setIsDrawerOpen(false)
        setSelectedPropertyId(null)
      }
    } catch (error) {
      console.error('Error deleting property:', error)
      alert(t('common.error'))
    } finally {
      setDeletingId(null)
    }
  }

  const handlePropertyClick = (id: string) => {
    setSelectedPropertyId(id)
    setIsDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setIsDrawerOpen(false)
    setSelectedPropertyId(null)
  }

  // Navigation functions
  const getCurrentIndex = useCallback(() => {
    if (!selectedPropertyId || !properties) return -1
    return properties.findIndex((p) => p.id === selectedPropertyId)
  }, [selectedPropertyId, properties])

  const handleNavigatePrevious = useCallback(() => {
    if (!properties || properties.length === 0) return
    const currentIndex = getCurrentIndex()
    if (currentIndex > 0) {
      const previousProperty = properties[currentIndex - 1]
      if (previousProperty) {
        setSelectedPropertyId(previousProperty.id)
      }
    }
  }, [properties, getCurrentIndex])

  const handleNavigateNext = useCallback(() => {
    if (!properties || properties.length === 0) return
    const currentIndex = getCurrentIndex()
    if (currentIndex >= 0 && currentIndex < properties.length - 1) {
      const nextProperty = properties[currentIndex + 1]
      if (nextProperty) {
        setSelectedPropertyId(nextProperty.id)
      }
    }
  }, [properties, getCurrentIndex])

  const hasPrevious = getCurrentIndex() > 0
  const hasNext = getCurrentIndex() >= 0 && getCurrentIndex() < (properties?.length ?? 0) - 1

  // Keyboard navigation
  useEffect(() => {
    if (!isDrawerOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        handleNavigatePrevious()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        handleNavigateNext()
      } else if (e.key === 'Escape') {
        handleDrawerClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isDrawerOpen, handleNavigatePrevious, handleNavigateNext])

  if (isLoading) {
    return <LoadingState message={t('common.loading')} />
  }

  if (error) {
    return <ErrorState message={t('common.error')} onRetry={() => refetch()} />
  }

  if (!properties || properties.length === 0) {
    return (
      <>
        <EmptyState
          title={t('properties.no_properties')}
          description={t('properties.create_first')}
          action={{
            label: t('properties.create'),
            onClick: () => setIsCreateModalOpen(true),
          }}
        />
        <PropertyCreateModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />
      </>
    )
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">{t('properties.title')}</Heading>
        <HStack gap={2}>
          <HStack gap={1} borderWidth="1px" borderRadius="md" p={1}>
            <IconButton
              aria-label={t('properties.view_mode.cards')}
              title={t('properties.view_mode.cards')}
              size="sm"
              variant={viewMode === 'cards' ? 'solid' : 'ghost'}
              onClick={() => setViewMode('cards')}
            >
              <LayoutGrid size={18} />
            </IconButton>
            <IconButton
              aria-label={t('properties.view_mode.table')}
              title={t('properties.view_mode.table')}
              size="sm"
              variant={viewMode === 'table' ? 'solid' : 'ghost'}
              onClick={() => setViewMode('table')}
            >
              <Table size={18} />
            </IconButton>
          </HStack>
          <Button colorPalette="brand" onClick={() => setIsCreateModalOpen(true)}>
            <HStack gap={2}>
              <Plus size={20} />
              <span>{t('properties.create')}</span>
            </HStack>
          </Button>
        </HStack>
      </HStack>

      {viewMode === 'cards' ? (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
          {properties.map((property) => (
            <PropertyCard
              key={property.id}
              property={property}
              isSelected={selectedPropertyId === property.id}
              onClick={handlePropertyClick}
              onDelete={
                deletingId === property.id || !canDeleteResidence(property.id)
                  ? undefined
                  : handleDelete
              }
            />
          ))}
        </SimpleGrid>
      ) : (
        <PropertyTable
          properties={properties}
          selectedPropertyId={selectedPropertyId}
          onPropertyClick={handlePropertyClick}
          onDelete={deletingId ? undefined : handleDelete}
          canDelete={canDeleteResidence}
        />
      )}

      <PropertyCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />

      <PropertyDetailDrawer
        propertyId={selectedPropertyId}
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        onNavigatePrevious={handleNavigatePrevious}
        onNavigateNext={handleNavigateNext}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
      />
    </Box>
  )
}
